import { useEffect, useState } from 'react';
import Cookie from 'js-cookie';
import styled from 'styled-components';

import { Button } from 'shared/components/Button';
import { Modal } from 'shared/components/Modal';
import { getKlarnaEnabledCountry } from 'checkout/utils/orderUtils';
import { PaymentVendor } from 'checkout/enums/PaymentVendor';
import { sendTrackingEvent } from 'tracking/utils/sendTrackingEvent';
import { STORAGE } from 'shared/enums/SitePreferences';
import { useCheckoutContext } from 'checkout/providers/CheckoutContextProvider';
import { t } from 'localization/utils/t';

const TP = 'checkout.components.Payment.ChangeCurrencyKlarnaModal';

interface IChangeCurrencyModal {
  selectPaymentVendor: (selectedPaymentVendor: PaymentVendor) => void;
}

export const ChangeCurrencyKlarnaModal = ({ selectPaymentVendor }: IChangeCurrencyModal) => {
  const [isLoading, setIsLoading] = useState(false);
  const { addresses, order, updateOrder } = useCheckoutContext();
  const selectedCountryCode = Cookie.getJSON(STORAGE.COUNTRY);
  const shippingCountryCode = addresses[order.addressId]?.countryCode;
  const { currency: userRegionCurrencyCode } =
    getKlarnaEnabledCountry(selectedCountryCode) ||
    getKlarnaEnabledCountry(shippingCountryCode) ||
    {};
  const selectedCurrencyCode = Cookie.getJSON(STORAGE.CURRENCY);
  const [isOpen, setIsOpen] = useState(selectedCurrencyCode !== userRegionCurrencyCode);

  const handleChangeCurrency = async () => {
    Cookie.set(STORAGE.CURRENCY, userRegionCurrencyCode);
    setIsLoading(true);
    await updateOrder({ orderNumber: order.number, payload: { updateCurrency: true } });
    setIsLoading(false);
    setIsOpen(false);
  };

  const handleCloseModal = () => {
    setIsOpen(false);
    selectPaymentVendor(PaymentVendor.CreditCard);
  };

  useEffect(() => {
    if (isOpen) {
      sendTrackingEvent('MODAL_OPEN_VIEW', {
        modal_name: 'update_currency',
      });
    }
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  return (
    <ChangeCurrencyKlarnaModal.Modal
      data-qa="change_currency_modal"
      onClose={handleCloseModal}
      title={t(`${TP}.changeCurrencyTitle`, 'Update Currency')}
    >
      <ChangeCurrencyKlarnaModal.Body>
        {t(
          `${TP}.changeCurrencyBodyKlarna`,
          'Klarna is available in {userRegionCurrencyCode} only. Would you like to update your currency preference to {userRegionCurrencyCode}?',
          { userRegionCurrencyCode },
        )}
      </ChangeCurrencyKlarnaModal.Body>
      <ChangeCurrencyKlarnaModal.Cta>
        <Button
          data-qa="change_currency_confirm_button"
          loading={isLoading}
          onClick={handleChangeCurrency}
        >
          {t(`${TP}.changeCurrencyKlarnaOk`, 'UPDATE')}
        </Button>
      </ChangeCurrencyKlarnaModal.Cta>
    </ChangeCurrencyKlarnaModal.Modal>
  );
};

ChangeCurrencyKlarnaModal.Modal = styled(Modal)`
  max-width: 335px;
`;

ChangeCurrencyKlarnaModal.Body = styled.p`
  margin: 8px 0 32px;
`;

ChangeCurrencyKlarnaModal.Cta = styled.div`
  display: flex;
`;
